/**
 * Shared slash-command metadata (Hermes `COMMAND_REGISTRY` pattern, Dayvise-adapted).
 *
 * This module owns names, descriptions, and arg hints only — no execution.
 * Execution lives in three places, split by whose state answers:
 * - server-answered (`where: "server"`): workspace-state queries and explicit
 *   user-intent actions handled in `packages/control-plane/src/slash-commands.ts`
 *   with no run and no model call;
 * - runtime-answered (`where: "runtime"`): run-local concerns handled inside the
 *   container (`plan` prompt-modal, durable `compress` operation);
 * - client-answered (`where: "client"`): the web composer answers locally with
 *   no message and no run — UI chrome verbs and pointers to the surface that
 *   actually holds the state (apps/web/src/clientSlash.ts).
 * This is the internal source inventory. Public help, docs and completion use
 * supportedSlashCommands; scope and implementation status are separate.
 * The server re-validates authorization independently of this metadata.
 */

export interface SlashCommandMeta {
  readonly name: string;
  readonly description: string;
  readonly argsHint: string | null;
  /** Who answers: the control plane without a run, the run container, or the client UI. */
  readonly where: "server" | "runtime" | "client";
}

export const SLASH_COMMANDS: readonly SlashCommandMeta[] = [
  { name: "help", description: "List available commands", argsHint: null, where: "server" },
  { name: "tools", description: "List or manage workspace tool permissions", argsHint: "[allow|approve|deny|reset <tool-id>]", where: "server" },
  { name: "skills", description: "List visible skills you can ask about", argsHint: null, where: "server" },
  { name: "status", description: "Show plan, workspace role, active runs and schedule counts", argsHint: null, where: "server" },
  { name: "usage", description: "Show usage totals per meter", argsHint: null, where: "server" },
  { name: "version", description: "Show the client protocol version", argsHint: null, where: "server" },
  { name: "goal", description: "Set or manage this conversation's goal", argsHint: "<objective>|status|pause|resume|complete|clear", where: "server" },
  { name: "loop", description: "Control the goal's autonomous continuation loop", argsHint: "start|stop|[every <N>m] <prompt>", where: "server" },
  { name: "subgoal", description: "Add a task to the live goal", argsHint: "<task>", where: "server" },
  { name: "tasks", description: "Show the live goal's tasks", argsHint: null, where: "server" },
  { name: "pause", description: "Pause the selected branch's active run", argsHint: null, where: "server" },
  { name: "resume", description: "Resume a paused or retryable run on the selected branch", argsHint: null, where: "server" },
  { name: "stop", description: "Stop the selected branch's active run", argsHint: null, where: "server" },
  { name: "whoami", description: "Show your identity, role and workspace", argsHint: null, where: "server" },
  { name: "sessions", description: "List recent conversations, or search the transcript", argsHint: "[query]", where: "server" },
  { name: "context", description: "Show this conversation's messages, run and goal state", argsHint: null, where: "server" },
  { name: "title", description: "Set the conversation title", argsHint: "<title>", where: "server" },
  { name: "memory", description: "Search memories (or list recent ones)", argsHint: "[query]", where: "server" },
  { name: "learn", description: "Record a private memory", argsHint: "<fact>", where: "server" },
  { name: "model", description: "List or select a model and reasoning effort for a branch, one task, or agent default", argsHint: "[provider:model|reset] [--provider <provider>] [--reasoning <level>] [--session|--once|--global]", where: "server" },
  { name: "approvals", description: "List pending tool approvals in this conversation", argsHint: null, where: "server" },
  { name: "retry", description: "Retry your last message as a new run", argsHint: null, where: "server" },
  { name: "heartbeat", description: "Alias of the goal loop's continuation cadence", argsHint: "start|stop|status", where: "server" },
  { name: "todos", description: "Show saved tasks on the selected branch without a model call", argsHint: null, where: "server" },
  { name: "plan", description: "Plan first: get a markdown plan with exact steps, no execution yet", argsHint: "<goal>", where: "runtime" },
  // Client-tier: parity with the bundled desktop slash registry. Descriptions
  // are the local answers too — one honest sentence per verb.
  { name: "approve", description: "Approve pending tool calls from the run's approval card.", argsHint: null, where: "client" },
  { name: "busy", description: "Presence is implicit in this client — runs always show their live state.", argsHint: null, where: "client" },
  { name: "clear", description: "Start a new conversation", argsHint: null, where: "client" },
  { name: "config", description: "Settings live in the sidebar: Providers, Storage and Tools.", argsHint: null, where: "client" },
  { name: "copy", description: "Copy text with your browser's copy command.", argsHint: null, where: "client" },
  { name: "cron", description: "Open scheduled tasks", argsHint: null, where: "client" },
  { name: "curator", description: "Show skill maintenance status and manage skill versions", argsHint: "[list|versions <skillId>|archive <skillId>|restore <skillId>]", where: "server" },
  { name: "deny", description: "Deny pending tool calls from the run's approval card.", argsHint: null, where: "client" },
  { name: "exit", description: "End the session by closing the tab or signing out.", argsHint: null, where: "client" },
  { name: "fast", description: "Model speed tiers are not available in this client; choose the model on the Agents page.", argsHint: null, where: "client" },
  { name: "footer", description: "This client has no footer chrome to toggle.", argsHint: null, where: "client" },
  { name: "gateway", description: "Messaging gateways are not available in this client.", argsHint: null, where: "client" },
  { name: "history", description: "Show recent conversation history on the selected branch.", argsHint: "[1-100]", where: "server" },
  { name: "image", description: "Image upload is not available in this client.", argsHint: null, where: "client" },
  { name: "indicator", description: "Run state is always shown in the stream status badge.", argsHint: null, where: "client" },
  { name: "insights", description: "Open workspace usage and cost", argsHint: null, where: "client" },
  { name: "kanban", description: "Open the private Kanban boards surface", argsHint: null, where: "client" },
  { name: "login", description: "You are already signed in to this workspace.", argsHint: null, where: "client" },
  { name: "paste", description: "Paste with your browser's paste command.", argsHint: null, where: "client" },
  { name: "platforms", description: "Messaging platforms are not available in this client.", argsHint: null, where: "client" },
  { name: "plugins", description: "Open registered MCP servers and visible skills", argsHint: null, where: "client" },
  { name: "quit", description: "End the session by closing the tab or signing out.", argsHint: null, where: "client" },
  { name: "reasoning", description: "Set branch or agent-default reasoning effort; show or hide reasoning in this client", argsHint: "[level|reset|show|hide] [--session|--global]", where: "server" },
  { name: "redraw", description: "The interface re-renders automatically.", argsHint: null, where: "client" },
  { name: "reload", description: "Reload the page to refresh its state.", argsHint: null, where: "client" },
  { name: "reload-mcp", description: "Refresh cached connected-tool data in this client", argsHint: null, where: "client" },
  { name: "reload-skills", description: "Refresh visible skill data in this client", argsHint: null, where: "client" },
  { name: "reload_mcp", description: "Alias of /reload-mcp.", argsHint: null, where: "client" },
  { name: "reload_skills", description: "Alias of /reload-skills.", argsHint: null, where: "client" },
  { name: "restart", description: "Send a message to start a new run.", argsHint: null, where: "client" },
  { name: "sb", description: "Alias of /statusbar.", argsHint: null, where: "client" },
  { name: "set-home", description: "The workspace root is fixed per workspace.", argsHint: null, where: "client" },
  { name: "sethome", description: "Alias of /set-home.", argsHint: null, where: "client" },
  { name: "snap", description: "Alias of /snapshot.", argsHint: null, where: "client" },
  { name: "snapshot", description: "Back up or restore agent configuration and skills only", argsHint: null, where: "client" },
  { name: "statusbar", description: "The status bar is always visible in this client.", argsHint: null, where: "client" },
  { name: "toolsets", description: "Open workspace tool permissions", argsHint: null, where: "client" },
  { name: "update", description: "Updates ship with each deployment.", argsHint: null, where: "client" },
  { name: "verbose", description: "Per-run event logs are not available in this client.", argsHint: null, where: "client" },
  { name: "voice", description: "Start an in-app voice conversation with structured secure input", argsHint: null, where: "client" },
  // Acp-parity rows (acp_adapter/commands.py): local answers that name the
  // surface holding the state.
  { name: "compress", description: "Summarize older context for future turns; keep the transcript intact.", argsHint: "[focus | here N] [--preview]", where: "runtime" },
  { name: "queue", description: "Queue prompts in order while this conversation stays open; status, retry or clear the local queue.", argsHint: "<prompt>|status|retry|clear", where: "client" },
  { name: "reset", description: "Start a new conversation from Conversations to reset the thread.", argsHint: null, where: "client" },
  { name: "steer", description: "Send an instruction to the selected live run without starting another run.", argsHint: "<instruction>", where: "client" },
  // Registry-parity rows for verbs whose Dayvise surface is a different shape
  // than the reference TUI/desktop client: one honest sentence per verb naming
  // the surface that actually holds the state.
  { name: "agents", description: "Open authorized agents and recent run progress", argsHint: null, where: "client" },
  { name: "battery", description: "Show battery status and freshness for registered account devices", argsHint: null, where: "server" },
  { name: "bg", description: "Backgrounding is implicit: runs continue server-side and reconnect on return.", argsHint: null, where: "client" },
  { name: "blueprint", description: "Preview and use versioned automation templates", argsHint: null, where: "client" },
  { name: "bp", description: "Alias of /blueprint.", argsHint: null, where: "client" },
  { name: "branch", description: "Create and select an independent conversation branch", argsHint: "[--here] [name]", where: "server" },
  { name: "browser", description: "Show browser grants or start an isolated headless browser task.", argsHint: "[URL or task]", where: "server" },
  { name: "btw", description: "Ask a side question in an isolated branch without touching the main task", argsHint: "<question>|list|status <questionId>|cancel <questionId>", where: "server" },
  { name: "bundles", description: "Manage versioned private skill bundles and explicit sharing", argsHint: null, where: "client" },
  { name: "codex-runtime", description: "Open Codex runtime and ChatGPT-plan connection status", argsHint: null, where: "client" },
  { name: "codex_runtime", description: "Alias of /codex-runtime.", argsHint: null, where: "client" },
  { name: "commands", description: "Alias of /help", argsHint: null, where: "client" },
  { name: "compact", description: "Summarize older context for future turns; keep the transcript intact.", argsHint: "[focus | here N] [--preview]", where: "runtime" },
  { name: "compose", description: "Compose multi-part messages in the composer and send them together.", argsHint: null, where: "client" },
  { name: "ctx", description: "Alias of /context.", argsHint: null, where: "client" },
  { name: "debug", description: "Per-run event logs are not available in this client.", argsHint: null, where: "client" },
  { name: "diff", description: "Inspect full versioned file and project differences", argsHint: null, where: "client" },
  { name: "egress", description: "Egress policy is enforced per workspace by the platform (prompt.md §61).",
    argsHint: null, where: "client" },
  { name: "export", description: "Export a redacted agent profile archive", argsHint: "[agent-id|agent name] [-o filename.tar.gz]", where: "server" },
  { name: "focus", description: "This client has no pane focus to manage.", argsHint: null, where: "client" },
  { name: "fork", description: "Create and select an independent conversation branch", argsHint: "[--here] [name]", where: "server" },
  { name: "generate-pet", description: "No novelty features ship in this client.", argsHint: null, where: "client" },
  { name: "handoff", description: "Hand work between conversations by sending the context link.", argsHint: null, where: "client" },
  { name: "hatch", description: "No novelty features ship in this client.", argsHint: null, where: "client" },
  { name: "hb", description: "Alias of /heartbeat.", argsHint: null, where: "client" },
  { name: "import", description: "Restore an uploaded profile as a new private agent", argsHint: "<file-id|workspace-file-path> [--name <name>]", where: "server" },
  { name: "init", description: "Scan the selected project directory and propose an AGENTS.md revision", argsHint: "[scan [directory]|list|status <jobId>|apply <jobId>|discard <jobId>]", where: "server" },
  { name: "journey", description: "Show your learning timeline oldest first", argsHint: null, where: "server" },
  { name: "learning", description: "List or summarise your private learning events", argsHint: "[list|status]", where: "server" },
  { name: "memory-graph", description: "Explore scoped memory relationships and provenance", argsHint: null, where: "client" },
    { name: "moa", description: "Ask several models independently and synthesise their answers", argsHint: "--models provider:model,provider:model [--synthesis provider:model] <question>|list|status <id>|cancel <id>", where: "server" },
  { name: "new", description: "Start a new conversation", argsHint: null, where: "client" },
  { name: "palette", description: "The composer's autocomplete is this client's command palette.", argsHint: null, where: "client" },
  { name: "personality", description: "List or select a named personality overlay", argsHint: "[name|none]", where: "server" },
  { name: "pet", description: "No novelty features ship in this client.", argsHint: null, where: "client" },
  { name: "platform", description: "Messaging platforms are not available in this client.", argsHint: null, where: "client" },
  { name: "proactive", description: "Open Call me when settings for presence-aware notifications and calls", argsHint: null, where: "client" },
  { name: "profile", description: "Identity and role are shown by /whoami.", argsHint: null, where: "client" },
  { name: "prompt", description: "Edit and send the next user message in a prompt editor.", argsHint: "[seed text]", where: "client" },
  { name: "q", description: "Ask a quick question as a normal message — every turn is stateless anyway.", argsHint: null, where: "client" },
  { name: "refine", description: "Create or manage a bounded memory or skill refinement", argsHint: "[list|status <id>|memory <memoryId>|skill <skillId>|apply <id>|revert <id>]", where: "server" },
  { name: "review", description: "Run an independent reviewer over one finished run's recorded change", argsHint: "<question>|list|status <reviewId>|cancel <reviewId>", where: "server" },
  { name: "rollback", description: "Preview and restore exact file or project versions", argsHint: null, where: "client" },
  { name: "save", description: "Save this branch's transcript as a downloadable file", argsHint: "[json|md|html] [filename] [redact]", where: "server" },
  { name: "skin", description: "Use the theme toggle; every client defaults to the jetblack dark theme.", argsHint: null, where: "client" },
  { name: "start", description: "Send a message to start working, or set a goal with /goal.", argsHint: null, where: "client" },
  { name: "subscription", description: "Open workspace plan and billing", argsHint: null, where: "client" },
  { name: "suggest", description: "Propose concrete automations from recorded recurring requests", argsHint: "[scan|list|status <suggestionId>|accept <suggestionId> [agent <agentId>]|dismiss <suggestionId>]", where: "server" },
  { name: "suggestions", description: "Alias of /suggest.", argsHint: null, where: "client" },
  { name: "timestamps", description: "Every transcript message carries its timestamp.", argsHint: null, where: "client" },
  { name: "topic", description: "Rename the conversation with /title.", argsHint: null, where: "client" },
  { name: "topup", description: "Plan and billing live on the Billing page.", argsHint: null, where: "client" },
  { name: "ts", description: "Alias of /timestamps.", argsHint: null, where: "client" },
  { name: "undo", description: "Return to the Nth recent user turn and restore its draft", argsHint: "[N]", where: "server" },
  { name: "upgrade", description: "Open workspace plan and billing", argsHint: null, where: "client" },
  { name: "v", description: "Alias of /version.", argsHint: null, where: "client" },
  { name: "wake", description: "Configure the personal Android Hey EVE entry path", argsHint: null, where: "client" },
  { name: "worktree", description: "Show the real isolated Git worktrees on this conversation", argsHint: null, where: "client" },
  { name: "yolo", description: "Manage cloud and explicitly device-granted filesystem reach with mandatory approvals", argsHint: null, where: "client" },
];

/** Aliases resolve before choosing the execution tier, including client → server. */
export const SLASH_ALIASES: Readonly<Record<string, string>> = {
  reload_mcp: "reload-mcp", reload_skills: "reload-skills", sethome: "set-home",
  sb: "statusbar", snap: "snapshot", bp: "blueprint", codex_runtime: "codex-runtime",
  ctx: "context", hb: "heartbeat", v: "version", commands: "help",
  reset: "clear", topic: "title", profile: "whoami", ts: "timestamps",
  suggestions: "suggest", compact: "compress",
};

export function canonicalSlashName(name: string): string {
  const lower = name.toLowerCase();
  return SLASH_ALIASES[lower] ?? lower;
}

export function slashCommandNames(): string[] {
  return supportedSlashCommands().map((command) => command.name);
}


/**
 * Owner-approved scope is independent from the internal source inventory above.
 * "planned" remains required work; it must not be sold as an executable command.
 * This catalogue never grants access to a tool, workspace, file, or provider.
 */
export type SlashCommandStatus = "available" | "planned" | "removed";
export type SlashClient = "web" | "desktop" | "android";

export const SLASH_COMMAND_SCOPE: Readonly<Record<string, SlashCommandStatus>> = {
  "help": "available",
  "tools": "available",
  "skills": "available",
  "status": "available",
  "usage": "available",
  "version": "available",
  "goal": "available",
  "loop": "available",
  "subgoal": "available",
  "tasks": "available",
  "pause": "available",
  "resume": "available",
  "stop": "available",
  "whoami": "available",
  "sessions": "available",
  "context": "available",
  "title": "available",
  "memory": "available",
  "learn": "available",
  "model": "available",
  "approvals": "available",
  "retry": "available",
  "heartbeat": "available",
  "todos": "available",
  "plan": "available",
  "approve": "available",
  "busy": "removed",
  "clear": "available",
  "config": "removed",
  "copy": "removed",
  "cron": "available",
  "curator": "available",
  "deny": "available",
  "exit": "removed",
  "fast": "removed",
  "footer": "removed",
  "gateway": "removed",
  "history": "available",
  "image": "removed",
  "indicator": "removed",
  "insights": "available",
  "kanban": "available",
  "login": "removed",
  "paste": "removed",
  "platforms": "removed",
  "plugins": "available",
  "quit": "removed",
  "reasoning": "available",
  "redraw": "removed",
  "reload": "removed",
  "reload-mcp": "available",
  "reload-skills": "available",
  "restart": "removed",
  "set-home": "removed",
  "snapshot": "available",
  "statusbar": "removed",
  "toolsets": "available",
  "update": "removed",
  "verbose": "removed",
  "voice": "planned",
  "compress": "available",
  "queue": "available",
  "steer": "available",
  "agents": "available",
  "battery": "available",
  "bg": "removed",
  "blueprint": "available",
  "branch": "available",
  "browser": "available",
  "btw": "available",
  "bundles": "available",
  "codex-runtime": "available",
  "compose": "removed",
  "debug": "removed",
  "diff": "available",
  "egress": "removed",
  "export": "available",
  "focus": "removed",
  "fork": "available",
  "generate-pet": "removed",
  "handoff": "removed",
  "hatch": "removed",
  "import": "available",
  "init": "available",
  "journey": "available",
  "learning": "available",
  "memory-graph": "available",
  "moa": "available",
  "new": "available",
  "palette": "removed",
  "personality": "available",
  "pet": "removed",
  "platform": "removed",
  "proactive": "available",
  "prompt": "removed",
  "q": "removed",
  "refine": "available",
  "review": "available",
  "rollback": "available",
  "save": "removed",
  "skin": "removed",
  "start": "removed",
  "subscription": "available",
  "suggest": "available",
  "timestamps": "removed",
  "topup": "removed",
  "undo": "available",
  "upgrade": "available",
  "wake": "planned",
  "worktree": "available",
  "yolo": "available",
};

/** Real client entry points; unavailable native pages stay out of native help. */
const CLIENT_AVAILABILITY: Readonly<Record<string, readonly SlashClient[]>> = {
  agents: ["web", "desktop", "android"],
  kanban: ["web", "desktop", "android"],
  approve: ["web", "desktop", "android"],
  deny: ["web", "desktop", "android"],
  clear: ["web", "desktop", "android"],
  new: ["web", "desktop", "android"],
  steer: ["web", "desktop", "android"],
  queue: ["web", "desktop", "android"],
  cron: ["web", "desktop", "android"],
  insights: ["web", "desktop"],
  plugins: ["web", "desktop"],
  subscription: ["web", "desktop"],
  toolsets: ["web", "desktop"],
  upgrade: ["web", "desktop"],
  "reload-mcp": ["web", "desktop"],
  "reload-skills": ["web", "desktop"],
  snapshot: ["web", "desktop"],
  "memory-graph": ["web", "desktop"],
  bundles: ["web", "desktop"],
  blueprint: ["web", "desktop"],
  proactive: ["web", "desktop"],
  yolo: ["web", "desktop"],
  diff: ["web", "desktop"],
  rollback: ["web", "desktop"],
  "codex-runtime": ["web", "desktop"],
  worktree: ["web", "desktop"],
};

export function slashCommandStatus(name: string): SlashCommandStatus | undefined {
  return SLASH_COMMAND_SCOPE[canonicalSlashName(name)];
}

/** Only completed actions are projected into public discovery. Aliases inherit. */
export function supportedSlashCommands(client?: SlashClient): SlashCommandMeta[] {
  return SLASH_COMMANDS.flatMap((command) => {
    const name = canonicalSlashName(command.name);
    if (slashCommandStatus(name) !== "available") return [];
    const target = SLASH_COMMANDS.find((candidate) => candidate.name === name);
    if (!target) return [];
    const clients = target.where === "client" ? CLIENT_AVAILABILITY[name] : undefined;
    if (target.where === "client" && (!clients || (client && !clients.includes(client)))) return [];
    return [{ ...command, description: target.description, where: target.where, argsHint: target.argsHint }];
  });
}

/**
 * Client spellings whose text a server caller may still send, because the
 * server-answered command of the same name is real behaviour that a headless or
 * older client legitimately uses (KANBAN board operations). Every other
 * client-tier command is a UI action and is refused as text.
 */
export const CLIENT_TIER_COMMANDS_WITH_TEXT_FALLBACK: readonly string[] = ["kanban"];

/** Historical prose wrappers, retained for source tracking rather than execution. */
export const PROSE_SLASH_COMMANDS = ["q", "start", "restart"] as const;

/**
 * Composer completion for a draft: the prefix after `/` up to the first space.
 * Prose, paths, and fully typed text return no suggestions.
 */
export function slashCompletions(draft: string, limit = 8, client: SlashClient = "web"): SlashCommandMeta[] {
  if (!draft.startsWith("/") || /\s/.test(draft)) return [];
  const query = draft.slice(1).split(/\s/, 1)[0]?.toLowerCase() ?? "";
  // An alias typed as its own prefix must surface its canonical entry: /bp is
  // how the alias is written, but the completion users need is /blueprint.
  const canonicalQuery = canonicalSlashName(query);
  return supportedSlashCommands(client).filter((command) => command.name.startsWith(query) || command.name.startsWith(canonicalQuery)).slice(0, limit);
}
